import { defineStore } from "pinia";
import joi from "joi";
import {
  fromUnixToAPIDate,
  getEndDate,
  getStartDate,
  toLongDateRange,
  toUnix,
} from "@/stories/utils/dates";
import { useAuthStore } from "@/stores/auth.module";
import md5 from "md5";
import type { IDefaultState, IMultiSelectItem } from "@/interfaces";
import type {
  StoreName,
  TabErrorSchemaTypes,
  IdType,
  RRuleSupportedEntityType,
  DefaultFilterType,
  SignupParticipants,
  MeetingSuggestionType,
  ListResourceType,
} from "@/types";
import helpers from "@/helpers";
import {
  type NotificationSettingsInput,
  type SummarizedMeetingsFragment,
  type MeetingInput,
  type EntityPagerFilter,
  type MeetingAgendaPointInput,
  Profile_Type,
  Sort_Field,
  Sort_Order,
  Module_Type,
} from "@/graphql/generated-types";
import { fixValidationFields } from "@/helpers/forms/validate";
import { globali18n } from "@/i18n";
import { DEFAULT_NOTIFICATION_SETTINGS } from "@/consts";
import PagerManager from "@/helpers/manager/PagerManager";
import api from "@/api";
import { useDefaults } from "@/composables/useDefaults";
import getNotificationSettings from "@/services/notications/getNotificationSettings";
import { useMeetingAgendaPoints } from "@/composables/useMeetingAgendaPoints";
import getFallbackImage from "@/helpers/files/getFallbackImage";
import {
  SHARING_FEATURE,
  type SharingFeatureState,
} from "@/helpers/features/SharingFeature";
import {
  PICTURES_FEATURE,
  type PicturesFeatureState,
} from "@/helpers/features/PicturesFeature";
import {
  RRULE_FEATURE,
  type RRuleFeatureState,
} from "@/helpers/features/RRuleFeature";
import ParticipantFeature, {
  type ParticipantFeatureState,
} from "@/helpers/features/ParticipantFeature";
import getTabCounts from "@/helpers/shared/getTabCounts";

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  startDate: joi.number().required(),
  endDate: joi.number().required().greater(joi.ref("startDate")),
  imageList: joi.array().min(1).max(1),
});

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title", "endDate"],
  pictures: ["image"],
  pdf: [],
};

const participantFeature = new ParticipantFeature<SignupParticipants>([
  "employeesSignedUp",
  "residentsSignedUp",
]);

export type MeetingItem = {
  id?: number;
  title: string;
  location: string;
  startDate: number;
  endDate: number;
  price?: string;
  vacation?: boolean;
  category?: string;
  isCancelled: boolean;
  signupEnabled: boolean;
  __typename: RRuleSupportedEntityType;
  agendaPoints: IMultiSelectItem[];
  suggestions: MeetingSuggestionType[];
  original: Partial<MeetingItem>;
} & SharingFeatureState &
  PicturesFeatureState &
  RRuleFeatureState &
  ParticipantFeatureState<SignupParticipants>;

export interface MeetingsListFilters {
  categories: string[];
  groups: string[];
  departments: string[];
}

export interface IMeetingsState extends IDefaultState {
  listFilters: MeetingsListFilters;
  item: MeetingItem;
  notification: NotificationSettingsInput;
  pager: PagerManager<SummarizedMeetingsFragment>;
  listResourceType: ListResourceType;
}

const getDefaultState = (): IMeetingsState => {
  const item: MeetingItem = {
    title: "",
    location: "",
    endDate: getEndDate(),
    category: undefined,
    isCancelled: false,
    signupEnabled: true,
    agendaPoints: [],
    suggestions: [],
    original: {
      startDate: getStartDate(),
      endDate: getEndDate(),
    },
    ...SHARING_FEATURE.getDefault(),
    ...PICTURES_FEATURE.getDefault(),
    ...RRULE_FEATURE.getDefault("SingleMeeting"),
    ...participantFeature.getDefault(),
  };

  return {
    listFilters: {
      categories: [],
      groups: [],
      departments: [],
    },
    tabErrors: {},
    originalHash: null,
    validation: null,
    pager: new PagerManager<SummarizedMeetingsFragment>(),
    item,
    notification: {
      ...DEFAULT_NOTIFICATION_SETTINGS,
    },
    listResourceType: "internal",
  };
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IMeetingsState => getDefaultState(),
    getters: {
      tabCounts: (state) => getTabCounts(state),
      employeeSuggestions(state) {
        return state.item.suggestions.filter(
          (item) =>
            item.profileType.toLowerCase() ===
            Profile_Type.Employee.toLowerCase(),
        );
      },
      residentSuggestions(state) {
        return state.item.suggestions.filter(
          (item) =>
            item.profileType.toLowerCase() ===
            Profile_Type.Resident.toLowerCase(),
        );
      },
    },
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash(): string {
        const data = {
          ...this.getItem(),
          notification: this.notification,
          ...SHARING_FEATURE.hash(this.item),
          ...PICTURES_FEATURE.hash(this.item),
          ...RRULE_FEATURE.hash(this.item as RRuleFeatureState),
        };
        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      async setItem(id: IdType) {
        // No need to refetch
        if (id && this.item.id?.toString() === id.toString()) {
          return;
        }

        const data = await api.meetings.getMeetingDetail(id);
        if (!data) {
          return;
        }

        this.item = {
          ...getDefaultState().item,
          id: parseInt(data.id),
          title: data.title,
          location: data.location || "",
          startDate: toUnix(new Date(data.startDate)),
          endDate: toUnix(new Date(data.endDate)),
          isCancelled: data.cancelled,
          signupEnabled: data.signupEnabled,
          __typename: data.__typename as RRuleSupportedEntityType,
          agendaPoints: (data.agendaPoints || []).map((agendaPoint) => {
            return {
              id: agendaPoint.id,
              title: agendaPoint.title,
              imageURL: agendaPoint.picture?.source,
              imageID: agendaPoint.picture?.id,
            } as IMultiSelectItem;
          }),
          suggestions: (data.suggestions || []).map((suggestion) => {
            return {
              id: suggestion.id,
              title: suggestion.title,
              status: suggestion.status,
              profileType: suggestion.suggester?.__typename as Profile_Type,
              profileName: suggestion.suggester?.displayName || "",
              profileImageURL: suggestion.suggester?.image?.source,
            } as MeetingSuggestionType;
          }),
          original: {
            startDate: toUnix(new Date(data.startDate)),
            endDate: toUnix(new Date(data.endDate)),
          },
          ...PICTURES_FEATURE.getState(data),
          ...participantFeature.getItem(data),
        };

        if (this.item.imageList.length === 0) {
          this.item.imageList.push(await getFallbackImage("meeting"));
        }

        SHARING_FEATURE.setState({
          data,
          state: this.item,
          moduleType: Module_Type.Meeting,
          onCompleted: this.setOriginalHash,
        });

        RRULE_FEATURE.setState(data, this.item as RRuleFeatureState);

        this.item.category = data.meetingCategory?.id || undefined;

        this.item.signupEnabled = data.signupEnabled;

        const { getMeetingNotificationSettings } = useDefaults();
        this.notification = getNotificationSettings(
          data,
          this.item,
          getMeetingNotificationSettings,
        );

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): MeetingInput {
        const authStore = useAuthStore();
        const item = { ...this.item };

        const getSuggestionsByType = (type: Profile_Type) => {
          return this.item.suggestions
            .filter(
              (item) => item.profileType.toLowerCase() === type.toLowerCase(),
            )
            .map((item) => {
              return {
                id: item.id,
                status: item.status,
              };
            });
        };

        const id = item?.id?.toString();

        const input: MeetingInput = {
          id,
          institution: authStore.institutionId,
          title: item.title,
          signup: item.signupEnabled,
          status: item.isCancelled ? 2 : 1,
          startDate: fromUnixToAPIDate(item.startDate),
          endDate: fromUnixToAPIDate(item.endDate),
          location: item.location,
          // We have to send NULL to make the field empty. Undefined doesn't work
          category: item.category || null,
          suggestionsByEmployees: getSuggestionsByType(Profile_Type.Employee),
          suggestionsByResidents: getSuggestionsByType(Profile_Type.Resident),
          ...SHARING_FEATURE.getItem(this.item, { useDepartments: true }),
          ...RRULE_FEATURE.getItem(this.item as RRuleFeatureState),
        };

        participantFeature.append(input, this.item);

        // Setting agenda points
        const { agendaPoints } = useMeetingAgendaPoints();
        input.agendaPoints = this.item.agendaPoints.map((item) => {
          const agendaPoint = agendaPoints.value.find((i) => i.id === item.id);
          return {
            title: agendaPoint?.title || item.title,
            picture: item.imageID || agendaPoint?.picture?.id,
          } as MeetingAgendaPointInput;
        });

        return input;
      },
      async hasStaleData() {
        return await participantFeature.hasStaleData(this.item, () =>
          api.meetings.getMeetingDetail(this.item.id),
        );
      },
      async isNotValid() {
        this.validation = helpers.forms.validate(VALIDATION_SCHEMA, {
          ...this.item,
          ...PICTURES_FEATURE.toValidation(this.item),
        });

        // We can't use usei18n() in stores.
        // https://stackoverflow.com/a/71449708
        const { t } = globali18n;

        // We should fix the validation labels
        fixValidationFields(this.validation, {
          startDate: t("global.start"),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async fetchItems(filter: DefaultFilterType) {
        const { dateFrom, dateTo } = toLongDateRange(
          filter === "show_upcoming",
        );

        const params: EntityPagerFilter = {
          dateFrom,
          dateTo,
          sorting: {
            field: Sort_Field.StartDate,
            order:
              filter === "show_upcoming" ? Sort_Order.Asc : Sort_Order.Desc,
          },
        };
        await this.pager.paginate(api.meetings.getMeetings, params, this);
      },
    },
  });
};

// View store
export const useMeetingsStore = createUseStore("MEETINGS");

// We use the same store structure for the create modal.
export const useAddMeetingStore = createUseStore("ADD_MEETINGS");

export type MeetingsStore = ReturnType<typeof useMeetingsStore>;
